import {useMutation,useQuery,useQueryClient} from "@tanstack/react-query";
import {api} from "../client";

export type HolidayType = "national"| "regional"| "company" | "optional";

export interface HolidayRow {
    id: string;
    name: string;
    date: string;
    type: HolidayType;
    description: string;
    status: "active" | "inactive";
    createdByFullName: string | null;
    createdAt: string | null;
    updatedAt: string | null;
}

export interface HolidayListResponse {
  results: HolidayRow[];
  nextCursor: string | null;
  prevCursor: string | null;
}

 
export interface HolidayInput {
  name: string;
  date: string; // "YYYY-MM-DD"
  type: HolidayType;
  description?: string;
}

export interface HolidayListParams {
  status?: "active" | "inactive" | "all" | "";
  year?: number | string;
  month?: number | string;
  type?: HolidayType | "";
  search?: string;
}



// Query keys 
 
export const holidaysListKey = (params: HolidayListParams = {}) =>
  ["holidays", "list", params] as const;
 
export const holidayDetailKey = (id: string) =>
  ["holidays", "detail", id] as const;
 
// Hooks 
 
export function useHolidays(params: HolidayListParams = {}) {
  return useQuery<HolidayListResponse>({
    queryKey: holidaysListKey(params),
    queryFn: () =>
      api
        .get<HolidayListResponse>("/holidays/", {
          params: {
            status: params.status || undefined,
            year: params.year || undefined,
            month: params.month || undefined,
            type: params.type || undefined,
            search: params.search || undefined,
          },
        })
        .then((r) => r.data),
  });
}
 
export function useHoliday(id: string | undefined) {
  return useQuery<HolidayRow>({
    queryKey: holidayDetailKey(id ?? ""),
    queryFn: () =>
      api.get<HolidayRow>(`/holidays/${id}/`).then((r) => r.data),
    enabled: !!id,
  });
}
 
export function useCreateHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: HolidayInput) =>
      api.post<HolidayRow>("/holidays/", input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["holidays"] }),
  });
}
 
export function useUpdateHoliday(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<HolidayInput>) =>
      api.patch<HolidayRow>(`/holidays/${id}/`, input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["holidays"] }),
  });
}
 
export function useDeleteHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete(`/holidays/${id}/`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["holidays"] }),
  });
}
 
export function useActivateHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<HolidayRow>(`/holidays/${id}/toggle-status/`, {}).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["holidays"] }),
  });
}
 
export function useDeactivateHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<HolidayRow>(`/holidays/${id}/toggle-status/`, {}).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["holidays"] }),
  });
}

export function useTodayHoliday(enabled = true) {
  return useQuery<HolidayRow | null>({
    queryKey: ["holidays", "today"],
    queryFn: () =>
      api.get<HolidayRow | null>("/holidays/today/").then((r) => r.data),
    enabled,
    staleTime: 1000 * 60 * 60,
  });
}